import { useEffect, useState } from 'react';
import { clearFromLocalStorage, loadFromLocalStorage, saveToLocalStorage } from '@utils/storage';
import logger from '@utils/logs';
import { isNewBrowserSession } from '@utils/session';
import { useTranslation } from 'react-i18next';

type EndpointState<T> = {
    data: T | null;
    loading: boolean;
    ttl?: number;
    lastResponseTime: Date | null;
};

type EndpointsState = {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    [key: string]: EndpointState<any>;
};

const initialEndpointState = { data: null, loading: false };

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const useEndpointState = (endpointKeys: any) => {
    const { i18n } = useTranslation();
    const isLoggedIn = loadFromLocalStorage('login') !== null;
    const [initiallyLoggedIn] = useState<boolean>(isLoggedIn);

    const endpoints = Object.keys(endpointKeys);

    const [endpointsState, setEndpointsState] = useState<EndpointsState>(() => {
        const initialState: EndpointsState = {};

        endpoints.forEach((key) => {
            const ttl = endpointKeys[key].ttl;
            const data = endpointKeys[key].storeOnLocalStorage ? loadFromLocalStorage(key) : initialEndpointState.data;
            initialState[key] = {
                data,
                loading: false,
                ttl,
                lastResponseTime: null,
            };
        });

        return initialState;
    });

    const [changedLoginStatus, setChangedLoginStatus] = useState(false);

    // if clearOnNewSession is true
    // clear data if it's a new browser session
    // or if user logged in
    // or if user logged out
    useEffect(() => {
        isLoggedIn !== initiallyLoggedIn && !changedLoginStatus && setChangedLoginStatus(true);

        const newBrowserSession = isNewBrowserSession();

        endpoints.forEach((key) => {
            const clearOnNewSession = endpointKeys[key].clearOnNewSession;
            if (
                clearOnNewSession &&
                (newBrowserSession ||
                    // user logged in or out
                    changedLoginStatus)
            ) {
                logger.log('hooks > useEndpointState > new session, clearing endpoint data', { key, clearOnNewSession, newBrowserSession, changedLoginStatus });

                clearEndpointData(key);
            }
        });

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoggedIn, changedLoginStatus]);

    // if clearOnLanguageChange is true
    // clear data if language changes
    useEffect(() => {
        endpoints.forEach((key) => {
            const clearOnLanguageChange = endpointKeys[key].clearOnLanguageChange;
            if (clearOnLanguageChange) {
                logger.log('hooks > useEndpointState > language changed, clearing endpoint data', { key });

                clearEndpointData(key);
            }
        });

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [i18n.language]);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const setEndpointState = (endpointKey: string, newState: Partial<EndpointState<any>>) => {
        logger.log('hooks > useEndpointState > setEndpointState', { endpointKey, newState });
        setEndpointsState((prev) => {
            const updatedState = {
                ...prev,
                [endpointKey]: { ...prev[endpointKey], ...newState, lastResponseTime: new Date() },
            };
            if (newState.data !== undefined && endpointKeys[endpointKey].storeOnLocalStorage) {
                saveToLocalStorage(endpointKey, newState.data, endpointKeys[endpointKey].ttl);
            }
            return updatedState;
        });
    };

    const getEndpointState = (endpointKey: string) => {
        const now = new Date();
        const ttl = endpointsState[endpointKey].ttl;
        const lastResponseTime = endpointsState[endpointKey].lastResponseTime?.getTime();
        const expireTime = ttl && lastResponseTime && lastResponseTime + ttl;

        if (ttl && expireTime && now.getTime() > expireTime) {
            // if item expired, delete the item from storage and return null
            logger.log('hooks > useEndpointState > data expired, deleting key from localStorage', { endpointKey });
            clearFromLocalStorage(endpointKey);
            return initialEndpointState;
        }

        return endpointsState[endpointKey] || initialEndpointState;
    };

    const clearEndpointData = (endpointKey: string) => {
        logger.log('hooks > useEndpointState > clearEndpointData', { endpointKey });
        setEndpointState(endpointKey, { data: null });
    };

    return {
        setEndpointState,
        getEndpointState,
    };
};
